/**********************
 * Content events Recordings
 *********************/

import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Alert,
} from "reactstrap"
import { Table as AntdTable, Tooltip, Form as AntdForm, Input as AntdInput } from "antd"
import {
  acCreateUpdateRecording,
  acDeleteRecording,
} from "helpers/backend_helper"
import {
  getLottieLoadingOptions,
  showToast,
  contentImageFormatter,
} from "utils/general"
import EventImagePicker from "../Components/EventImagePicker"
import * as Yup from "yup"
import { useFormik } from "formik"
import Lottie from "react-lottie"
const { TextArea } = AntdInput;


const RecordingsContent = props => {
  const [selectedRecording, setSelectedRecording] = useState(null)
  const [selectedRecordingId, setSelectedRecordingId] = useState(null)

  const [recordingModal, setRecordingModal] = useState(false)
  const [deleteRecordingModal, setDeleteRecordingModal] = useState(false)

  const urlFlags = [
    { key: "autoplay", label: props.t("Autoplay") },
    { key: "mute", label: props.t("Mute") },
    { key: "loop", label: props.t("Loop") },
    { key: "controls", label: props.t("Show controls") },
    { key: "rel", label: props.t("Show related videos") },
  ]

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (!recordingModal) recordingValidation.resetForm()
  }, [recordingModal])

  // Delete recording
  const deleteRecording = () => {
    if (!props.ongoingAction && selectedRecordingId != null) {
      props.setOngoingAction(true)
      let id = Number.parseInt(selectedRecordingId, 10)
      acDeleteRecording(abortRef.current, id, props.isProduction)
        .then(result => {
          //console.log("Delete result: ", result)
          if (result.statusCode == 200) {
            showToast(props.t("Deleted"), "success")
            props.getRecordings(props.selectedTreeOption)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          props.setOngoingAction(false)
          setDeleteRecordingModal(false)
          setSelectedRecordingId(null)
        })
    }
  }

  // New recording validation init
  const setNewRecording = () => {
    let newEntry = new Object()
    newEntry.id = undefined
    newEntry.name = ""
    newEntry.url = ""
    newEntry.imageBase64 = ""

    setSelectedRecording(newEntry)
    setRecordingModal(true)
  }

  const getUrlType = u => {
    if (!u) return "unknown"
    try {
      const p = new URL(/^https?:\/\//i.test(u) ? u : `https://${u}`)
      const h = p.hostname.replace(/^www\./, "")
      if (h.includes("dreambroker.com")) return "dreambroker"
      if (h.includes("youtube.com") || h === "youtu.be") return "youtube"
    } catch { }
    return "unknown"
  }

  const TIME_REGEX = /^(?:\d{1,2}|\d{1,2}:\d{2}|\d{1,2}:\d{2}:\d{2})$/

  // Recording validation
  const recordingValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedRecording?.id || undefined,
      name: selectedRecording?.name || "",
      url: selectedRecording?.url || "",
      urlType:
        selectedRecording?.urlType || getUrlType(selectedRecording?.url || ""),
      imageBase64: selectedRecording?.imageBase64 || "",
      advancedMode: selectedRecording?.advancedMode || false,
      autoplay: selectedRecording?.autoplay || false,
      mute: selectedRecording?.mute || false,
      loop: selectedRecording?.loop || false,
      controls:
        selectedRecording?.controls !== undefined
          ? selectedRecording.controls
          : true,
      rel: selectedRecording?.rel !== undefined ? selectedRecording.rel : true,
      startInput: selectedRecording?.startInput || "",
      endInput: selectedRecording?.endInput || "",
      startSeconds: selectedRecording?.startSeconds ?? 0,
      endSeconds: selectedRecording?.endSeconds ?? 0,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(props.t("Required field"))
        .trim()
        .min(5, props.t("Too short"))
        .max(30, props.t("Too long")),
      url: Yup.string()
        .required(props.t("Required field"))
        .test(
          "is-valid-url",
          props.t("URL invalid"),
          value =>
            recordingValidation.values.advancedMode ||
            !!convertToEmbedUrl(value)
        ),
      startInput: Yup.string().test(
        "time-format",
        props.t("Invalid format"),
        v => !v || TIME_REGEX.test(v)
      ),
      endInput: Yup.string()
        .test(
          "time-format",
          props.t("Invalid format"),
          v => !v || TIME_REGEX.test(v)
        )
        .test(
          "end-after-start",
          props.t("End time must be less than start"),
          function (v) {
            const { startInput } = this.parent
            const startSec = parseTime(startInput)
            const endSec = parseTime(v)
            if (!endSec || !startSec) return true
            return endSec > startSec
          }
        ),
    }),

    onSubmit: values => {
      //console.log("Submit recording: ", values)

      let embededUrl = recordingValidation.values.advancedMode
        ? values.url
        : convertToEmbedUrl(values.url, {
          autoplay: values.autoplay,
          mute: values.mute,
          loop: values.loop,
          controls: values.controls,
          rel: values.rel,
          startSeconds: values.startSeconds,
          endSeconds: values.endSeconds,
        })

      if (!embededUrl) {
        recordingValidation.setFieldError("url", props.t("Invalid URL"))
        return
      }

      let imageChanged = values.imageBase64 != selectedRecording?.imageBase64

      if (!props.ongoingAction && props.selectedTreeOption) {
        props.setOngoingAction(true)
        acCreateUpdateRecording(
          abortRef.current,
          props.selectedTreeOption,
          values.name,
          embededUrl,
          imageChanged ? values.imageBase64 : undefined,
          values.id,
          props.isProduction
        )
          .then(result => {
            console.log("Add or update recording result: ", result)
            if (result.statusCode === 200 && result.id) {
              props.getRecordings(props.selectedTreeOption)
              setRecordingModal(false)
              showToast(props.t("Success"), "success")
            } else if (result.error_code === 3152) {
              showToast(props.t("Not changed"))
            } else if (result.error_code === 3145) {
              showToast(props.t("URL not valid"), "error")
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            props.setOngoingAction(false)
          })
      }
    },
  })

  const recordingColumns = [
    {
      dataIndex: "image_base64",
      title: props.t("Image"),
      align: "center",
      render: contentImageFormatter,
      width: "50px",
    },
    {
      dataIndex: "name",
      title: props.t("Name"),
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "url",
      title: props.t("Url"),
      render: urlFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "actions",
      title: props.t("Actions"),
      align: "center",
      render: recordingEditFormatter,
      //fixed: "right",
      width: "100px",
      ellipsis: true,
    },
  ]

  // Convert a string in hh:mm:ss foramt into total seconds
  const parseTime = val => {
    if (!val) return 0

    // just seconds
    if (/^\d+$/.test(val)) return Number(val)

    // hh:mm:ss to seconds
    if (val.includes(":")) {
      return val
        .split(":")
        .reduce((total, part) => total * 60 + Number(part), 0) // fold into seconds
    }

    return seconds
  }

  // Format seconds back into a string displayable in input
  const fmtTime = seconds => {
    if (!seconds) return ""

    const h = Math.floor(seconds / 3600) // hours
    const m = Math.floor((seconds % 3600) / 60) // minutes
    const s = seconds % 60 // seconds

    // hh:mm:ss if hours exist
    if (h > 0)
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`

    // mm:ss if minutes exist
    if (m > 0) return `${m}:${String(s).padStart(2, "0")}`

    // just seconds
    return String(s).padStart(2, "0")
  }

  function recordingEditFormatter(cell, row) {
    return (
      <>
        <Tooltip
          title={props.t("Edit")}
          placement="bottom"
          trigger={["hover", "focus"]}
        >
          <Button
            color="primary"
            outline
            size="sm"
            onClick={() => {
              let entry = {}
              entry.id = row.id
              entry.name = row.name
              entry.url = row.url
              entry.imageBase64 = row.image_base64

              try {
                const params = new URL(row.url).searchParams
                entry.autoplay = params.get("autoplay") === "1"
                entry.mute = params.get("mute") === "1"
                entry.loop = params.get("loop") === "1"
                entry.controls = params.get("controls") !== "0"
                entry.rel = params.get("rel") !== "0"

                // start/end extraction + display
                const startS = parseTime(
                  params.get("start") ||
                  params.get("t") ||
                  params.get("time_continue")
                )
                const endS = parseTime(params.get("end"))

                Object.assign(entry, {
                  startSeconds: startS,
                  endSeconds: endS,
                  startInput: fmtTime(startS),
                  endInput: fmtTime(endS),
                })

                const h = new URL(row.url).hostname.replace(/^www\./, "")
                entry.urlType = h.includes("dreambroker.com")
                  ? "dreambroker"
                  : h.includes("youtube.com") || h === "youtu.be"
                    ? "youtube"
                    : "unknown"
              } catch (err) {
                showToast(props.t("Error", err), "error")
                entry.autoplay = false
                entry.mute = false
                entry.loop = false
                entry.controls = true
                entry.rel = true
              }
              setSelectedRecording(entry)
              setRecordingModal(true)
            }}
          >
            <i className="mdi mdi-pencil" />
          </Button>
        </Tooltip>
        <Tooltip
          title={props.t("Delete")}
          placement="bottom"
          trigger={["hover", "focus"]}
        >
          <Button
            className="ms-2"
            color="danger"
            outline
            size="sm"
            onClick={() => {
              setSelectedRecordingId(row.id)
              setDeleteRecordingModal(true)
            }}
          >
            <i className="mdi mdi-delete" />
          </Button>
        </Tooltip>
      </>
    )
  }

  function urlFormatter(cell, row) {
    if (cell) {
      return (
        <a href={cell} target="_blank" rel="noreferrer">
          {props.t("Link")}
        </a>
      )
    } else return cell
  }

  const convertToEmbedUrl = (url, options = {}) => {
    if (!url) return null

    let formattedUrl = url
    if (!/^https?:\/\//i.test(url)) formattedUrl = "https://" + url

    let embedUrl = null

    try {
      const parsedUrl = new URL(formattedUrl)
      const hostname = parsedUrl.hostname.replace(/^www\./, "")

      if (hostname.includes("youtube.com") || hostname === "youtu.be") {
        let videoId
        if (hostname === "youtu.be") {
          videoId = parsedUrl.pathname.slice(1)
        } else if (parsedUrl.pathname.startsWith("/watch")) {
          videoId = parsedUrl.searchParams.get("v")
        } else if (parsedUrl.pathname.startsWith("/embed/")) {
          videoId = parsedUrl.pathname.split("/")[2]
        }

        if (videoId) {
          const params = new URLSearchParams()

          if (options.autoplay) params.set("autoplay", "1")
          if (options.mute) params.set("mute", "1")
          if (options.loop) {
            params.set("loop", "1")
            params.set("playlist", videoId)
          }
          if (options.controls === false) params.set("controls", "0")
          if (options.rel === false) params.set("rel", "0")
          if (options.startSeconds > 0)
            params.set("start", String(options.startSeconds))
          if (options.endSeconds > 0)
            params.set("end", String(options.endSeconds))

          embedUrl = `https://www.youtube.com/embed/${videoId}?${params.toString()}`
        }
      }

      if (hostname.includes("dreambroker.com")) {
        const parts = parsedUrl.pathname.split("/").filter(Boolean)
        const channelIndex = parts.indexOf("channel")
        if (channelIndex !== -1 && parts[channelIndex + 1]) {
          const channelId = parts[channelIndex + 1]
          let videoId =
            parts[channelIndex + 2] === "iframe"
              ? parts[channelIndex + 3]
              : parts[channelIndex + 2]
          if (videoId) {
            embedUrl = `https://dreambroker.com/channel/${channelId}/iframe/${videoId}`
          }
        }
      }
    } catch (err) {
      showToast(props.t("Error", err), "error")
      return null
    }

    return embedUrl
  }

  // Time input formatter
  const handleTimeChange = (fieldInput, fieldSeconds) => e => {
    // Allow only digits and colons
    const raw = e.target.value.replace(/[^0-9:]/g, "")
    recordingValidation.setFieldValue(fieldInput, raw)

    let seconds = 0
    const parts = raw.split(":").map(Number)

    if (parts.length === 3) {
      const [hh = 0, mm = 0, ss = 0] = parts
      seconds = hh * 3600 + mm * 60 + ss
    } else if (parts.length === 2) {
      const [mm = 0, ss = 0] = parts
      seconds = mm * 60 + ss
    } else {
      seconds = Number(raw) || 0
    }

    recordingValidation.setFieldValue(fieldSeconds, seconds)
    // console.log(`${fieldInput}:`, raw, "→", seconds)
  }

  return (
    <>
      {/* ACCORDION */}
      {props.loadingRecordings ? (
        <>
          <div className="text-center">
            <Lottie
              options={getLottieLoadingOptions()}
              height={80}
              width={80}
            />
          </div>
        </>
      ) : (
        <>
          <Row>
            <Col></Col>
            <Col className="col-auto">
              <Button
                color="success"
                size="sm"
                className="d-flex"
                style={{ alignItems: "center" }}
                onClick={() => {
                  setNewRecording()
                }}
              >
                <i className="me-2 bx bx-calendar-plus text-truncate align-middle"></i>
                <span className="">{props.t("New recording")}</span>
              </Button>
            </Col>
          </Row>
          <Row className="mt-2 mb-3">
            <Col>
              <AntdTable
                className="clear-header"
                size="small"
                sticky
                rowKey={"id"}
                columns={recordingColumns}
                dataSource={props.recordings}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: 50,
                  hideOnSinglePage: true,
                }}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: "35vh",
                }}
              />
            </Col>
          </Row>
        </>
      )}

      {/* MODALS */}
      <Modal
        isOpen={recordingModal}
        toggle={() => {
          setRecordingModal(!recordingModal)
        }}
        centered
      >
        <div className="modal-header">
          {selectedRecording?.id != undefined ? (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("Edit recording")}
            </h5>
          ) : (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("New recording")}
            </h5>
          )}
          <button
            type="button"
            onClick={() => {
              setRecordingModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <>
          <div className="modal-body">
            <Form
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <FormGroup>
                <Label>{props.t("Name")}</Label>
                <AntdForm.Item
                  validateStatus={recordingValidation.errors.name ? "error" : ""}
                  hasFeedback
                  help={recordingValidation.errors.name}
                >
                  <AntdInput
                    allowClear
                    showCount
                    maxLength={30}
                    placeholder={props.t("Name")}
                    value={recordingValidation.values.name}
                    onChange={(v) => { recordingValidation.setFieldValue("name", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
              <FormGroup>
                <Alert className="mt-4 mb-0" color="info">
                  {props.t("URL format info")}
                </Alert>
              </FormGroup>
              <FormGroup>
                <Label>{props.t("URL")}</Label>
                <Row>
                  <Col className="pe-0">
                    <AntdForm.Item
                      validateStatus={recordingValidation.errors.url ? "error" : ""}
                      hasFeedback
                      help={recordingValidation.errors.url}
                    >
                      <TextArea
                        allowClear
                        placeholder={props.t("URL")}
                        value={recordingValidation.values.url}
                        onChange={(v) => { recordingValidation.setFieldValue("url", v.target.value) }}
                        onBlur={e => {
                          recordingValidation.handleBlur(e)
                          const type = getUrlType(e.target.value)
                          recordingValidation.setFieldValue("urlType", type)
                        }}
                      />
                    </AntdForm.Item>
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center ps-0">
                    <Tooltip
                      title={props.t("Disable automatic URL formatting")}
                      trigger={["hover", "focus"]}
                    >
                      <div
                        className="form-check form-switch mt-1"
                        style={{
                          transform: "rotate(90deg)",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input input-outline"
                          id="advancedModeSwitch"
                          checked={recordingValidation.values.advancedMode}
                          onChange={e =>
                            recordingValidation.setFieldValue(
                              "advancedMode",
                              e.target.checked
                            )
                          }
                        />
                      </div>
                    </Tooltip>
                  </Col>
                </Row>
                {recordingValidation.touched.url &&
                  recordingValidation.errors.url ? (
                  <FormFeedback type="invalid">
                    {recordingValidation.errors.url}
                  </FormFeedback>
                ) : null}
              </FormGroup>

              <FormGroup>
                <Label
                  className="mt-1"
                  check
                  style={
                    recordingValidation.values.advancedMode ||
                      recordingValidation.values.urlType === "dreambroker"
                      ? {
                        color: "#6c757d",
                        opacity: 0.6,
                        pointerEvents: "none",
                      }
                      : {}
                  }
                >
                  {props.t("Youtube link settings")}
                </Label>
                <Row>
                  {urlFlags.map(({ key, label }) => (
                    <Col xs="auto" key={key}>
                      <FormGroup check>
                        <Label
                          check
                          style={
                            recordingValidation.values.advancedMode ||
                              recordingValidation.values.urlType === "dreambroker"
                              ? {
                                color: "#6c757d",
                                opacity: 0.6,
                                pointerEvents: "none",
                              }
                              : {}
                          }
                        >
                          <Input
                            className="input-outline"
                            type="checkbox"
                            name={key}
                            disabled={
                              recordingValidation.values.advancedMode ||
                              recordingValidation.values.urlType ===
                              "dreambroker"
                            }
                            checked={recordingValidation.values[key]}
                            onChange={recordingValidation.handleChange}
                          />
                          {label}
                        </Label>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              </FormGroup>

              <FormGroup style={{ marginTop: -10 }}>
                <Row>
                  <Col xs="auto">
                    <FormGroup>
                      <Label
                        style={
                          recordingValidation.values.advancedMode ||
                            recordingValidation.values.urlType === "dreambroker"
                            ? {
                              color: "#6c757d",
                              opacity: 0.6,
                              pointerEvents: "none",
                            }
                            : {}
                        }
                      >
                        {props.t("Starts from")}
                      </Label>
                      <Input
                        name="startInput"
                        inputMode="numeric"
                        placeholder="00:00"
                        disabled={
                          recordingValidation.values.advancedMode ||
                          recordingValidation.values.urlType === "dreambroker"
                        }
                        value={recordingValidation.values.startInput}
                        onChange={handleTimeChange(
                          "startInput",
                          "startSeconds"
                        )}
                        onBlur={recordingValidation.handleBlur}
                        invalid={
                          !!(
                            recordingValidation.touched.startInput &&
                            recordingValidation.errors.startInput
                          )
                        }
                      />

                      {recordingValidation.touched.startInput &&
                        recordingValidation.errors.startInput && (
                          <FormFeedback type="invalid">
                            {recordingValidation.errors.startInput}
                          </FormFeedback>
                        )}
                    </FormGroup>
                  </Col>
                  <Col xs="auto">
                    <FormGroup>
                      <Label
                        style={
                          recordingValidation.values.advancedMode ||
                            recordingValidation.values.urlType === "dreambroker"
                            ? {
                              color: "#6c757d",
                              opacity: 0.6,
                              pointerEvents: "none",
                            }
                            : {}
                        }
                      >
                        {props.t("Ends at")}
                      </Label>
                      <Input
                        name="endInput"
                        inputMode="numeric"
                        placeholder="00:00:00"
                        disabled={
                          recordingValidation.values.advancedMode ||
                          recordingValidation.values.urlType === "dreambroker"
                        }
                        value={recordingValidation.values.endInput}
                        onChange={handleTimeChange("endInput", "endSeconds")}
                        onBlur={recordingValidation.handleBlur}
                        invalid={
                          !!(
                            recordingValidation.touched.endInput &&
                            recordingValidation.errors.endInput
                          )
                        }
                      />
                      {recordingValidation.touched.endInput &&
                        recordingValidation.errors.endInput && (
                          <FormFeedback type="invalid">
                            {recordingValidation.errors.endInput}
                          </FormFeedback>
                        )}
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>

              <EventImagePicker
                selected={recordingValidation.values.imageBase64}
                selectionChanged={img => {
                  recordingValidation.setFieldValue("imageBase64", img)
                }}
              />

              <ModalFooter className="p-2 mt-2">
                <Col lg={12} className="mt-2 mb-0  text-truncate">
                  <Row>
                    <Col xs={12}>
                      <span className="float-end">
                        <button
                          type="button"
                          onClick={() => {
                            setRecordingModal(false)
                          }}
                          className="btn btn-outline-primary "
                          data-dismiss="modal"
                        >
                          {props.t("Cancel")}
                        </button>

                        <button
                          disabled={props.ongoingAction}
                          className="btn btn-primary ms-2"
                          onClick={() => {
                            recordingValidation.submitForm()
                          }}
                        >
                          {props.t("Save")}
                        </button>
                      </span>
                    </Col>
                  </Row>
                </Col>
              </ModalFooter>
            </Form>
          </div>
        </>
      </Modal>
      {/** Delete recording modal */}
      <Modal
        isOpen={deleteRecordingModal}
        toggle={() => {
          setDeleteRecordingModal(!deleteRecordingModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Delete recording")}
          </h5>
          <button
            type="button"
            onClick={() => {
              setDeleteRecordingModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {props.t("Are you sure you want to delete the selected recording?")}
        </ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteRecordingModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("Cancel")}
                  </button>

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-danger ms-2"
                    onClick={() => {
                      deleteRecording()
                    }}
                  >
                    {props.t("Delete")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>
    </>
  )
}

RecordingsContent.propTypes = {
  t: PropTypes.any,
  isProduction: PropTypes.any,
  ongoingAction: PropTypes.any,
  setOngoingAction: PropTypes.any,
  recordings: PropTypes.any,
  getRecordings: PropTypes.func,
  loadingRecordings: PropTypes.any,
  selectedTreeOption: PropTypes.any,
}

export default withTranslation()(RecordingsContent)
